import { useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useSendEmailOtp, useVerifyEmailOtp } from "@/features/auth/services";
import { isApiError } from "@/shared/api";
import {
  clearPendingEmailVerification,
  getPendingEmailVerification,
  setPendingEmailVerification,
  type EmailVerificationSource,
} from "../utils/email-verification";
import { shouldAutoSendEmailOtp } from "../utils/email-verification-flow";
import Button from "../../../shared/components/Button";
import LinearLayout from "../../../shared/components/LinearLayout";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "../../../shared/components/ui/input-otp";

const CODE_LENGTH = 6;

const getRedirect = (value?: string | null) =>
  value && value.length > 0 ? value : null;

export default function EmailVerificationCodeForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const locationState =
    (location.state as {
      email?: string;
      redirectTo?: string;
      redirectPath?: string;
      otpSentAt?: number;
      source?: EmailVerificationSource;
    } | null) ?? null;
  const rememberedVerification = getPendingEmailVerification();
  const email =
    locationState?.email ??
    searchParams.get("email") ??
    rememberedVerification?.email ??
    "";
  const normalizedEmail = email.trim();
  const redirectPath =
    getRedirect(locationState?.redirectPath) ??
    getRedirect(locationState?.redirectTo) ??
    getRedirect(searchParams.get("redirectTo")) ??
    getRedirect(rememberedVerification?.redirectPath) ??
    null;
  const source = locationState?.source ?? rememberedVerification?.source;
  const otpSentAt =
    locationState?.otpSentAt ?? rememberedVerification?.otpSentAt ?? null;
  const [verificationCode, setVerificationCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync: verifyEmailOtp, isPending } = useVerifyEmailOtp();
  const { mutateAsync: resendEmailOtp, isPending: isResending } =
    useSendEmailOtp();
  const hasAttemptedAutoSend = useRef(false);

  useEffect(() => {
    if (!normalizedEmail) {
      return;
    }

    setPendingEmailVerification(normalizedEmail, {
      redirectPath: redirectPath ?? undefined,
      otpSentAt: otpSentAt ?? undefined,
      source,
    });
  }, [normalizedEmail, otpSentAt, redirectPath, source]);

  useEffect(() => {
    hasAttemptedAutoSend.current = false;
  }, [normalizedEmail, otpSentAt, source]);

  useEffect(() => {
    if (
      !normalizedEmail ||
      hasAttemptedAutoSend.current ||
      !shouldAutoSendEmailOtp({
        source,
        otpSentAt,
      })
    ) {
      return;
    }

    hasAttemptedAutoSend.current = true;

    resendEmailOtp({ email: normalizedEmail })
      .then(() => {
        const now = Date.now();
        setPendingEmailVerification(normalizedEmail, {
          redirectPath: redirectPath ?? undefined,
          otpSentAt: now,
          source,
        });
      })
      .catch(() => {
        // Keep the page usable for manual resend.
      });
  }, [normalizedEmail, otpSentAt, redirectPath, resendEmailOtp, source]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!normalizedEmail) {
      setError("Missing email. Please request a new code.");
      return;
    }

    if (verificationCode.trim().length !== CODE_LENGTH) {
      setError("Enter the 6-digit verification code.");
      return;
    }

    try {
      await verifyEmailOtp({
        email: normalizedEmail,
        otp: verificationCode.trim(),
      });
      toast.success("Email verified successfully. Please sign in to continue.");
      clearPendingEmailVerification();

      navigate("/sign-in", {
        replace: true,
        state: redirectPath ? { from: redirectPath } : undefined,
      });
    } catch (submissionError) {
      let message = "Unable to verify the code. Please try again.";
      let requestId: string | undefined;

      if (isApiError(submissionError)) {
        message = submissionError.message;
        requestId = submissionError.requestId;
      } else if (submissionError instanceof Error) {
        message = submissionError.message;
      }

      toast.error(message, {
        description: requestId ? `Request ID: ${requestId}` : undefined,
      });
      setError(message);
    }
  };

  const handleResend = async () => {
    if (!normalizedEmail) {
      setError("Missing email. Please request a new code.");
      return;
    }

    try {
      await resendEmailOtp({ email: normalizedEmail });
      const now = Date.now();
      setPendingEmailVerification(normalizedEmail, {
        redirectPath: redirectPath ?? undefined,
        otpSentAt: now,
        source,
      });
      toast.success("A new verification code has been sent.");
    } catch (submissionError) {
      let message = "Unable to resend the code. Please try again.";
      let requestId: string | undefined;

      if (isApiError(submissionError)) {
        message = submissionError.message;
        requestId = submissionError.requestId;
      } else if (submissionError instanceof Error) {
        message = submissionError.message;
      }

      toast.error(message, {
        description: requestId ? `Request ID: ${requestId}` : undefined,
      });
      setError(message);
    }
  };

  const maskedEmail = useMemo(() => {
    if (!normalizedEmail) return null;
    const [name, domain] = normalizedEmail.split("@");
    if (!domain) return normalizedEmail;
    const maskedName =
      name.length <= 2
        ? `${name.charAt(0)}*`
        : `${name.slice(0, 2)}${"*".repeat(Math.max(name.length - 2, 1))}`;
    return `${maskedName}@${domain}`;
  }, [normalizedEmail]);

  if (!normalizedEmail) {
    return (
      <div className="w-full">
        <LinearLayout orientation="vertical">
          <div className="text-center align-middle mx-auto space-y-5">
            <h1 className="text-white text-[20px] leading-[24px] font-medium">
              Email Verification
            </h1>
            <p className="text-white text-sm font-sm opacity-70">
              We could not find the email address for this verification flow.
              Please start again to request a new code.
            </p>
            <Button
              variant="primary"
              fluid
              onClick={() => {
                const redirectQuery = redirectPath
                  ? `?redirectTo=${encodeURIComponent(redirectPath)}`
                  : "";
                navigate(`/email-verification${redirectQuery}`);
              }}>
              Go Back
            </Button>
          </div>
        </LinearLayout>
      </div>
    );
  }

  return (
    <div className="w-full">
      <LinearLayout orientation="vertical">
        <form
          onSubmit={handleSubmit}
          className="text-center align-middle mx-auto space-y-4">
          <h1 className="text-white text-[20px] leading-[24px] font-medium">
            Email Verification
          </h1>
          <p className="text-white text-sm font-sm opacity-50">
            Enter the verification code sent to
            <span className="font-semibold text-white"> {maskedEmail}</span>
          </p>
          <InputOTP
            maxLength={CODE_LENGTH}
            value={verificationCode}
            onChange={(value) => {
              setVerificationCode(value);
              setError(null);
            }}
            containerClassName="justify-center"
            className="text-white">
            <InputOTPGroup className="gap-3">
              {Array.from({ length: CODE_LENGTH }).map((_, index) => (
                <InputOTPSlot
                  key={index}
                  index={index}
                  className="bg-transparent rounded-lg! border text-lg text-white size-10"
                />
              ))}
            </InputOTPGroup>
          </InputOTP>
          {error && <p className="text-danger text-sm text-center">{error}</p>}
          <div className="mb-6">
            <button
              type="button"
              onClick={handleResend}
              disabled={isResending}
              className="underline text-[14px] text-white font-sm disabled:opacity-60">
              {isResending ? "Resending..." : "Resend Code"}
            </button>
          </div>
          <Button
            variant="primary"
            fluid
            type="submit"
            loading={isPending}
            disabled={verificationCode.trim().length !== CODE_LENGTH}>
            {isPending ? "Verifying..." : "Verify Code"}
          </Button>
        </form>
      </LinearLayout>
    </div>
  );
}
